import React, { useState, useCallback, useMemo } from "react";
import './HighPriorityPage.scss';
import { Warning, Fire, MapPinArea, InfoIcon, Clock } from "@phosphor-icons/react";
import Spinner from '../../../components/Spinner';
import FlatList from 'flatlist-react/lib';
import HighPriorityFire from "./HighPriorityFire";
import refreshIcon from '../../../assets/icons/refresh-icon.png';

const MemoizedFire = React.memo(HighPriorityFire);

export default function HighPriorityPage({ panel, setPanel, map, user, modal, setModal, setUser, highPriorityFires, getHighPriorityFires, loading }) {
    const [search, setSearch] = useState('');

    const filteredFires = useMemo(() => {
        if (!highPriorityFires) return [];
        return highPriorityFires.filter(fire => {
            const fireName = fire.name ? fire.name.toLowerCase() : '';
            return fireName.includes(search.toLowerCase());
        });
    }, [highPriorityFires, search]);

    // Calculate statistics
    const stats = useMemo(() => {
        if (!highPriorityFires) return { total: 0, critical: 0, warning: 0 };
        
        const total = highPriorityFires.length;
        const critical = highPriorityFires.filter(fire => 
            fire.areas && fire.areas.some(area => area.distance <= 1)
        ).length;
        const warning = total - critical;
        
        return { total, critical, warning };
    }, [highPriorityFires]);

    const renderFireRow = useCallback(
    (fire) => (
        <MemoizedFire
            fire={fire}
            panel={panel}
            setPanel={setPanel}
            map={map}
            user={user}
            setUser={setUser}
            modal={modal}
            setModal={setModal}
        />
    ),
        [panel, setPanel, map, user, setUser, modal, setModal]
    );
    
    return (
        <div className='high-priority-page'>
            <div className='high-priority-page-title'>
                <div className='high-priority-page-title-text'>High Priority</div>
            </div>
            <div className='high-priority-page-description'>
                Immediate, proximity-based wildfire alerts for fires within five miles of your areas - crucial situational intelligence at a glance.
            </div>
            
            {/* Statistics Cards */}
            {highPriorityFires && highPriorityFires.length > 0 && (
                <div className='high-priority-page-stats'>
                    <div className='high-priority-page-stats-card total'>
                        <div className='high-priority-page-stats-card-icon'>
                            <Fire size={20} weight="fill" color="#e82700" />
                        </div>
                        <div className='high-priority-page-stats-card-content'>
                            <div className='high-priority-page-stats-card-number'>{stats.total}</div>
                            <div className='high-priority-page-stats-card-label'>Total Fires</div>
                        </div>
                    </div>
                    <div className='high-priority-page-stats-card critical'>
                        <div className='high-priority-page-stats-card-icon'>
                            <Warning size={20} weight="fill" color="#dc3545" />
                        </div>
                        <div className='high-priority-page-stats-card-content'>
                            <div className='high-priority-page-stats-card-number'>{stats.critical}</div>
                            <div className='high-priority-page-stats-card-label'>Critical (≤1mi)</div>
                        </div>
                    </div>
                    <div className='high-priority-page-stats-card warning'>
                        <div className='high-priority-page-stats-card-icon'>
                            <Warning size={20} weight="fill" color="#ffc107" />
                        </div>
                        <div className='high-priority-page-stats-card-content'>
                            <div className='high-priority-page-stats-card-number'>{stats.warning}</div>
                            <div className='high-priority-page-stats-card-label'>Warning (1-5mi)</div>
                        </div>
                    </div>
                </div>
            )}
            {/* <div className='high-priority-page-info'>
                <div className='high-priority-page-info-section'>
                    <div className='high-priority-page-info-section-title'>
                        <MapPinArea size={16} weight="fill" color='#e82700'/>
                        Proximity Monitoring
                    </div>
                    <div className='high-priority-page-info-section-description'>
                        Fires within 5 miles of your monitoring areas are automatically flagged as high priority for immediate attention.
                    </div>
                </div>

            </div> */}
            
            {highPriorityFires && (
                <div className='high-priority-page-search'>
                    <input
                        type="text"
                        className="high-priority-page-search-input"
                        placeholder="Search by fire name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <img
                        src={refreshIcon}
                        className="high-priority-page-refresh-icon"
                        style={{
                            transform: loading ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                            cursor: 'pointer'
                        }}
                        onClick={getHighPriorityFires}
                        title="Refresh data"
                    />
                </div>
            )}

            {loading ?
            <div style={{ paddingTop: '1rem' }}>
                <Spinner />
            </div>
            :
            highPriorityFires &&
            <div className='high-priority-page-content'>
                <div className="all-fires-list">
                    <FlatList
                        list={filteredFires}
                        renderItem={renderFireRow}
                        renderOnScroll
                        renderWhenEmpty={() => (
                            <div className='high-priority-page-empty-state'>
                                <div className='high-priority-page-empty-state-icon'>
                                    <Fire size={48} color="#ccc" weight="light" />
                                </div>
                                <div className='high-priority-page-empty-state-title'>
                                    {search ? 'No matching fires found' : 'No high priority fires'}
                                </div>
                                <div className='high-priority-page-empty-state-description'>
                                    {search 
                                        ? 'Try adjusting your search criteria or clear the search to see all fires.'
                                        : 'Great news! No fires are currently within 5 miles of your monitoring areas.'
                                    }
                                </div>
                            </div>
                        )}
                    />
                </div>
            </div>}
        </div>
    );
}
